import React from "react";
import { Box, Typography, Button, styled } from "@mui/material";
import { FlashIcon } from "../../icons/FlashIcon";
import type { MSHatData, ActivityCompletion } from "../../../types";

interface JobGearCoverContentProps {
  hat: MSHatData;
  onStart: () => void;
  activityCompletion?: ActivityCompletion | null;
  xpValue: number;
}

const ContentContainer = styled(Box, {
  label: "JobGearCoverContentContainer",
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
  width: "100%",
  maxWidth: "1280px",
  padding: theme.spacing(3),
}));

const MainTitle = styled(Typography, {
  label: "JobGearMainTitle",
})(() => ({
  fontFamily: "Nunito",
  fontWeight: 700,
  fontSize: "32px",
  lineHeight: "48px",
  color: "#ffffff",
  textAlign: "center",
  width: "381px",
}));

const XPSection = styled(Box, {
  label: "JobGearXPSection",
})(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
}));

const XPText = styled(Typography, {
  label: "JobGearXPText",
})(() => ({
  fontFamily: "Proxima Nova",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "28px",
  color: "#118d57",
}));

const CoverImage = styled(Box, {
  label: "JobGearCoverImage",
})(({ theme }) => ({
  width: "562px",
  height: "313px",
  borderRadius: theme.spacing(2),
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  border: "4px solid #ffffff",
  boxShadow:
    "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)",
  maxWidth: "100%",
  "@media (max-width: 600px)": {
    width: "100%",
    height: "200px",
  },
}));

const DescriptionText = styled(Typography, {
  label: "JobGearDescriptionText",
})(() => ({
  fontFamily: "Proxima Nova",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "28px",
  color: "#ffffff",
  textAlign: "center",
  width: "571px",
  maxWidth: "100%",
}));

const ButtonContainer = styled(Box, {
  label: "JobGearButtonContainer",
})(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  gap: theme.spacing(7),
  width: "100%",
}));

const StartButton = styled(Button, {
  label: "JobGearStartButton",
})(({ theme }) => ({
  backgroundColor: "#078dee",
  color: "#ffffff",
  fontFamily: "Proxima Nova",
  fontWeight: 700,
  fontSize: "15px",
  lineHeight: "26px",
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(1),
  minWidth: "157px",
  height: "48px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#0570c4",
  },
}));

export const JobGearCoverContent = ({
  hat,
  onStart,
  activityCompletion,
  xpValue,
}: JobGearCoverContentProps) => {
  const image = hat.jobGearDisplayImage?.url;

  return (
    <ContentContainer>
      <MainTitle>Job Gear</MainTitle>

      <XPSection>
        <FlashIcon width={24} color="#118d57" />
        <XPText>
          {activityCompletion && <span>You Earned </span>}
          {xpValue} XP
        </XPText>
      </XPSection>

      {image && (
        <CoverImage
          sx={{
            backgroundImage: `url('${image}')`,
          }}
        />
      )}

      <DescriptionText>
        Discover the tools, skills, and gear you&apos;ll use as a{" "}
        {hat.displayName}
      </DescriptionText>

      <ButtonContainer>
        <StartButton onClick={onStart}>
          {activityCompletion ? "Try Again" : "Start"}
        </StartButton>
      </ButtonContainer>
    </ContentContainer>
  );
};
